import type {
    PlanSummaryForAnalysis,
    ConvertToPlanSummaryResult,
} from './types';
import type { RawRiskReport, ReportState } from './client-report/types';
import { IStorageService } from '../../app-common/services/local-storage-service';
import { getLogger } from '../../app-common/util/logger';
import { CREDITS_EXHAUSTED_MESSAGE, isCreditsExhausted } from '../../app-common/util/ai-credit-errors';

const logger = getLogger('intelligence');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CpmAnalysisCallbacks {
    onThinking: (text: string) => void;
    onAnalysis: (text: string) => void;
    onComplete: (report: RawRiskReport, analysisText: string) => void;
    onError: (message: string) => void;
}

export interface CpmAnalysisCancellable {
    cancel: () => void;
}

export interface CpmAnalysisInput {
    convertedData: ConvertToPlanSummaryResult;
    projectContext: string;
    planId: string;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const CpmIntelligenceServiceName = 'gpCpmIntelligenceService';

export interface ICpmIntelligenceService {
    analyzeRisk(
        input: CpmAnalysisInput,
        callbacks: CpmAnalysisCallbacks,
    ): CpmAnalysisCancellable;
}

class CpmIntelligenceService implements ICpmIntelligenceService {
    static ServiceName = CpmIntelligenceServiceName;
    static $inject = ['$timeout', 'gpLocalStorageService'];

    private serviceBase = (window as any).__gameplan_service_url || 'http://localhost/';

    constructor(
        private readonly $timeout: ng.ITimeoutService,
        private readonly storage: IStorageService,
    ) { }

    analyzeRisk(
        input: CpmAnalysisInput,
        callbacks: CpmAnalysisCallbacks,
    ): CpmAnalysisCancellable {
        const abortController = new AbortController();

        const { convertedData, projectContext, planId } = input;
        const planSummary = convertedData.planSummary;

        logger.debug(`analyzeRisk() — plan: "${planSummary.planName}", ${planSummary.tasks.length} tasks, ${planSummary.groups.length} groups`);

        this.doAnalyzeRisk(
            planId, planSummary, projectContext, convertedData,
            callbacks, abortController.signal,
        );

        return {
            cancel: () => {
                logger.debug('cancel() — aborting fetch');
                abortController.abort();
            },
        };
    }

    private async doAnalyzeRisk(
        planId: string,
        planSummary: PlanSummaryForAnalysis,
        projectContext: string,
        convertedData: ConvertToPlanSummaryResult,
        callbacks: CpmAnalysisCallbacks,
        signal: AbortSignal,
    ): Promise<void> {
        try {
            const authData = this.storage.getAsObject<{ token: string }>('authorizationData') || { token: '' };
            const url = this.serviceBase + 'api/gameplan/projects/analyze-risk-cpm';

            const body = JSON.stringify({
                planId,
                planSummary,
                projectContext,
                taskIdMap: convertedData.taskIdMap,
                groupIdMap: convertedData.groupIdMap,
                resourceIdMap: convertedData.resourceIdMap,
                milestoneIdMap: convertedData.milestoneIdMap,
                meetingIdMap: convertedData.meetingIdMap,
            });

            logger.debug(`POST ${url} — body: ${body.length} chars`);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'bearer ' + authData.token,
                },
                body,
                signal,
            });

            logger.debug(`fetch response — status: ${response.status}, ok: ${response.ok}`);

            if (!response.ok) {
                logger.error(`server error — status: ${response.status}`);
                // The credit-enforcement gate (#74) refuses with 402 before the
                // stream starts — show the friendly top-up message, not "Server error".
                const message = isCreditsExhausted(response.status)
                    ? CREDITS_EXHAUSTED_MESSAGE
                    : `Server error: ${response.status}`;
                this.$timeout(() => callbacks.onError(message));
                return;
            }

            const reader = response.body!.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let analysisText = '';
            let readCount = 0;
            let totalEvents = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    logger.debug(`stream done — ${readCount} reads, ${totalEvents} events parsed`);
                    break;
                }

                readCount++;
                buffer += decoder.decode(value, { stream: true });

                const events = this.parseSSEBuffer(buffer);
                buffer = events.remainder;

                if (events.parsed.length > 0) {
                    logger.debug(`read #${readCount} — ${events.parsed.length} events: [${events.parsed.map(e => e.type).join(', ')}]`);
                }

                for (const event of events.parsed) {
                    totalEvents++;
                    this.handleSSEEvent(event, callbacks, analysisText, (text) => { analysisText = text; });
                }
            }

            // Process any remaining buffer
            if (buffer.trim()) {
                const events = this.parseSSEBuffer(buffer + '\n\n');
                for (const event of events.parsed) {
                    this.handleSSEEvent(event, callbacks, analysisText, (text) => { analysisText = text; });
                }
            }

        } catch (err: any) {
            if (err.name === 'AbortError') {
                logger.debug('fetch aborted');
                return;
            }
            logger.error('fetch error:', err);
            this.$timeout(() => callbacks.onError(err.message || 'Analysis failed'));
        }
    }

    private handleSSEEvent(
        event: { type: string; data: string },
        callbacks: CpmAnalysisCallbacks,
        analysisText: string,
        setAnalysisText: (text: string) => void,
    ): void {
        try {
            const parsed = JSON.parse(event.data);

            if (event.type === 'thinking') {
                logger.debug(`SSE thinking — ${(parsed.text || '').length} chars: "${(parsed.text || '').substring(0, 80)}..."`);
                this.$timeout(() => callbacks.onThinking(parsed.text));
            } else if (event.type === 'analysis') {
                logger.debug(`SSE analysis — ${(parsed.text || '').length} chars`);
                setAnalysisText(analysisText + (parsed.text || ''));
                this.$timeout(() => callbacks.onAnalysis(parsed.text));
            } else if (event.type === 'complete') {
                logger.debug(`SSE complete — risks: ${parsed.report?.risks?.length ?? 0}`);
                const report: RawRiskReport = parsed.report || { risks: [] };
                this.$timeout(() => callbacks.onComplete(report, analysisText));
            } else if (event.type === 'error') {
                logger.error(`SSE error — ${parsed.message}`);
                this.$timeout(() => callbacks.onError(parsed.message));
            } else if (event.type === 'debug') {
                logger.debug(`SSE debug — model: ${parsed.model}`);
            } else if (event.type === 'reportId') {
                logger.debug(`SSE reportId — ${parsed.reportId}`);
            } else {
                logger.debug(`SSE unknown event type: "${event.type}"`);
            }
        } catch (err) {
            logger.error(`SSE parse error for event type "${event.type}":`, err);
        }
    }

    private parseSSEBuffer(buffer: string): { parsed: { type: string; data: string }[]; remainder: string } {
        const parsed: { type: string; data: string }[] = [];
        const blocks = buffer.split('\n\n');
        const remainder = blocks.pop() || '';

        for (const block of blocks) {
            if (!block.trim()) continue;

            let eventType = 'message';
            let data = '';

            const lines = block.split('\n');
            for (const line of lines) {
                if (line.startsWith('event: ')) {
                    eventType = line.slice(7).trim();
                } else if (line.startsWith('data: ')) {
                    data = line.slice(6);
                }
            }

            if (data) {
                parsed.push({ type: eventType, data });
            }
        }

        return { parsed, remainder };
    }
}

export { CpmIntelligenceService };
